import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  getWebUsers,
  createWebUser,
  updateWebUser,
  deleteWebUser,
  resetUserPassword,
} from "../../api/webUserAPI";
import { useTranslation } from "react-i18next";

const formatDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString("tr") + " " + d.toLocaleTimeString("tr");
};

export default function UsersDataGrid() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    group1: "",
    group2: "",
    group3: "",
    roles: "",
    readOnlyAccess: 0,
    readWriteAccess: 0,
    enabled: true,
  });
  const [addFormData, setAddFormData] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    group1: "",
    group2: "",
    group3: "",
    roles: "",
    readOnlyAccess: 0,
    readWriteAccess: 0,
    enabled: true,
    emailVerified: true,
    createdTimestamp: 0,
    totp: true,
    disableableCredentialTypes: [],
    requiredActions: [],
    notBefore: 0,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [passwordResetForm, setPasswordResetForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getWebUsers();
        setUsers(response || []);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err.message || "Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    setRows(
      users
        .filter((u) => u.username !== "admin@letta.com.tr")
        .map((u) => ({
          ...u,
          date: formatDate(u.createdTimestamp),
        }))
    );
  }, [users]);

  const handleMenuClick = (event, rowId) => {
    console.log("Selected Row ID:", rowId);
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't reset selectedRowId here, keep it for edit dialog
  };

  const columns = [
    {
      field: "username",
      headerName: t("users.dataGrid.username"),
      width: 150,
      flex: 1.2,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "firstName",
      headerName: t("users.dataGrid.firstName"),
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "lastName",
      headerName: t("users.dataGrid.lastName"),
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "email",
      headerName: t("users.dataGrid.email"),
      width: 200,
      flex: 1.2,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group1",
      headerName: t("users.dataGrid.group1"),
      width: 120,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group2",
      headerName: t("users.dataGrid.group2"),
      width: 120,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group3",
      headerName: t("users.dataGrid.group3"),
      width: 120,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "date",
      headerName: t("users.dataGrid.date"),
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "operations",
      headerName: t("users.dataGrid.operations"),
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={(event) => handleMenuClick(event, params.row.id)}
            size="small"
          >
            <MoreHorizIcon />
          </IconButton>
        );
      },
    },
  ];

  const handleEdit = () => {
    const selectedUser = users.find((user) => user.id === selectedRowId);
    if (selectedUser) {
      setEditFormData({
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        email: selectedUser.email || "",
        username: selectedUser.username || "",
        group1: selectedUser.group1 || "",
        group2: selectedUser.group2 || "",
        group3: selectedUser.group3 || "",
        roles: Array.isArray(selectedUser.roles)
          ? selectedUser.roles[0] || ""
          : selectedUser.roles || "",
        readOnlyAccess: selectedUser.readOnlyAccess || 0,
        readWriteAccess: selectedUser.readWriteAccess || 0,
        enabled:
          selectedUser.enabled !== undefined ? selectedUser.enabled : true,
      });
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (window.confirm(t("users.messages.deleteConfirm"))) {
      try {
        setLoading(true);
        await deleteWebUser(selectedRowId);
        console.log("User deleted successfully");
        // Refresh the users list
        const response = await getWebUsers();
        setUsers(response || []);
      } catch (error) {
        console.error("Error deleting user:", error);
        // Optionally show error message to user
        alert(
          t("users.messages.deleteFailed") +
            ": " +
            (error.message || "Unknown error")
        );
      } finally {
        setLoading(false);
      }
    }
    handleMenuClose();
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditFormData({
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      group1: "",
      group2: "",
      group3: "",
      roles: "",
      readOnlyAccess: 0,
      readWriteAccess: 0,
      enabled: true,
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditSubmit = async () => {
    try {
      setEditLoading(true);
      // Get the current user data
      const currentUser = users.find((user) => user.id === selectedRowId);
      if (!currentUser) {
        throw new Error("User not found");
      }

      // Convert roles string to array format for API
      const dataToSend = {
        ...currentUser,
        ...editFormData,
        roles: editFormData.roles ? [editFormData.roles] : [],
      };

      const response = await updateWebUser(dataToSend);

      if (response.returnCode === -1) {
        throw new Error(response.exMessage || "Update failed");
      }

      // Optionally show success message
      console.log("User updated successfully");

      // Refresh the users list
      const usersResponse = await getWebUsers();
      setUsers(usersResponse || []);
    } catch (error) {
      console.error("Error updating user:", error);
      alert(error.message || "Failed to update user");
    } finally {
      setEditLoading(false);
      handleEditDialogClose(); // Dialog will always close after save attempt
    }
  };

  const handleRoleChange = (event) => {
    const value = event.target.value;
    handleEditFormChange("roles", value);
  };

  // Add user handlers
  const handleAddUser = () => {
    setAddDialogOpen(true);
    setFormErrors({});
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
    setAddFormData({
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      group1: "",
      group2: "",
      group3: "",
      roles: "",
      readOnlyAccess: 0,
      readWriteAccess: 0,
      enabled: true,
      emailVerified: true,
      createdTimestamp: 0,
      totp: true,
      disableableCredentialTypes: [],
      requiredActions: [],
      notBefore: 0,
    });
    setFormErrors({});
  };

  const handleAddFormChange = (field, value) => {
    setAddFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateAddForm = () => {
    const errors = {};

    if (!addFormData.username.trim()) {
      errors.username = "Username is required";
    }

    if (!addFormData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(addFormData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Debug log for roles
    console.log("Validating roles:", addFormData.roles);

    if (!addFormData.roles || addFormData.roles.trim() === "") {
      errors.roles = "A role is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddSubmit = async () => {
    if (!validateAddForm()) {
      return;
    }

    try {
      setAddLoading(true);
      // Convert roles string to array format for API
      const dataToSend = {
        ...addFormData,
        roles: addFormData.roles ? [addFormData.roles] : [],
      };

      const response = await createWebUser(dataToSend);

      if (response.returnCode === -1) {
        throw new Error(response.exMessage || "Add user failed");
      }

      console.log("User added successfully");
      handleAddDialogClose();

      // Refresh the users list
      const usersResponse = await getWebUsers();
      setUsers(usersResponse || []);
    } catch (error) {
      console.error("Error adding user:", error);
      setFormErrors({ submit: error.message || "Failed to add user" });
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddRoleChange = (event) => {
    const value = event.target.value;

    setAddFormData((prev) => ({
      ...prev,
      roles: value,
    }));

    // Clear roles error if role is selected
    if (value && value.trim() !== "" && formErrors.roles) {
      setFormErrors((prev) => ({
        ...prev,
        roles: null,
      }));
    }
  };

  const handleResetPassword = async () => {
    console.log("Selected Row ID (User ID):", selectedRowId);
    setPasswordResetDialogOpen(true);
    handleMenuClose();
  };

  const handlePasswordResetDialogClose = () => {
    setPasswordResetDialogOpen(false);
    setPasswordResetForm({
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handlePasswordResetFormChange = (field, value) => {
    setPasswordResetForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordResetSubmit = async () => {
    if (!passwordResetForm.newPassword) {
      setSnackbar({
        open: true,
        message: t("users.messages.passwordRequired") || "Password is required",
        severity: "error",
      });
      return;
    }

    if (passwordResetForm.newPassword !== passwordResetForm.confirmPassword) {
      setSnackbar({
        open: true,
        message:
          t("users.messages.passwordMismatch") || "Passwords do not match",
        severity: "error",
      });
      return;
    }

    try {
      setPasswordResetLoading(true);
      const response = await resetUserPassword(
        selectedRowId,
        passwordResetForm.newPassword
      );

      if (response.returnCode === -1) {
        throw new Error(response.exMessage || "Reset password failed");
      }

      console.log("Password reset successfully for user ID:", selectedRowId);

      setSnackbar({
        open: true,
        message:
          t("users.messages.passwordResetSuccess") ||
          "Password has been reset successfully",
        severity: "success",
      });

      handlePasswordResetDialogClose();
    } catch (error) {
      console.error("Error resetting password:", error);
      setSnackbar({
        open: true,
        message:
          t("users.messages.passwordResetFailed") +
          ": " +
          (error.message || "Unknown error"),
        severity: "error",
      });
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Box sx={{ maxWidth: { sm: "100%", md: "1700px" } }}>
        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6" fontWeight="bold" sx={{ opacity: 0 }}>
                Placeholder
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ opacity: 0 }}>
                Placeholder
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {t("users.header")}
              </Typography>
              <Box flex={1} />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddUser}
                sx={{ height: 45, width: 200 }}
              >
                {t("users.controls.addUser")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ height: "75%", width: "100%", position: "relative" }}>
        {loading && rows.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" align="center" sx={{ my: 2 }}>
            {typeof error === "string" ? error : t("users.messages.fetchError")}
          </Typography>
        )}

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          slots={{ toolbar: GridToolbar }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100 },
            },
            filter: {
              filterModel: {
                items: [{ field: "code", operator: "contains", value: "" }],
              },
            },
          }}
          loading={loading && rows.length === 0}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("common.edit")} />
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("common.delete")} />
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          <ListItemIcon>
            <RestartAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("users.menu.resetPassword")} />
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("users.dialogs.edit.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label={t("users.fields.username")}
              value={editFormData.username}
              onChange={(e) => handleEditFormChange("username", e.target.value)}
              fullWidth
            />
            <TextField
              label={t("users.fields.firstName")}
              value={editFormData.firstName}
              onChange={(e) =>
                handleEditFormChange("firstName", e.target.value)
              }
              fullWidth
            />
            <TextField
              label={t("users.fields.lastName")}
              value={editFormData.lastName}
              onChange={(e) => handleEditFormChange("lastName", e.target.value)}
              fullWidth
            />
            <TextField
              label={t("users.fields.email")}
              value={editFormData.email}
              onChange={(e) => handleEditFormChange("email", e.target.value)}
              fullWidth
              type="email"
            />
            <TextField
              label={t("users.fields.group1")}
              value={editFormData.group1}
              onChange={(e) => handleEditFormChange("group1", e.target.value)}
              fullWidth
            />
            <TextField
              label={t("users.fields.group2")}
              value={editFormData.group2}
              onChange={(e) => handleEditFormChange("group2", e.target.value)}
              fullWidth
            />
            <TextField
              label={t("users.fields.group3")}
              value={editFormData.group3}
              onChange={(e) => handleEditFormChange("group3", e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t("users.fields.roles")}</InputLabel>
              <Select value={editFormData.roles} onChange={handleRoleChange}>
                <MenuItem value="user">{t("users.roles.user")}</MenuItem>
                <MenuItem value="admin">{t("users.roles.admin")}</MenuItem>
                <MenuItem value="serviceUser">
                  {t("users.roles.serviceUser")}
                </MenuItem>
                <MenuItem value="superAdmin">
                  {t("users.roles.superAdmin")}
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t("users.fields.readOnlyAccess")}
              value={editFormData.readOnlyAccess}
              onChange={(e) =>
                handleEditFormChange(
                  "readOnlyAccess",
                  parseInt(e.target.value) || 0
                )
              }
              fullWidth
              type="number"
            />
            <TextField
              label={t("users.fields.readWriteAccess")}
              value={editFormData.readWriteAccess}
              onChange={(e) =>
                handleEditFormChange(
                  "readWriteAccess",
                  parseInt(e.target.value) || 0
                )
              }
              fullWidth
              type="number"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.enabled}
                  onChange={(e) =>
                    handleEditFormChange("enabled", e.target.checked)
                  }
                />
              }
              label={t("users.fields.enabled")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>{t("common.cancel")}</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleAddDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("users.dialogs.add.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {formErrors.submit && (
              <Typography color="error" variant="body2">
                {formErrors.submit}
              </Typography>
            )}
            <TextField
              label={t("users.fields.username")}
              value={addFormData.username}
              onChange={(e) => handleAddFormChange("username", e.target.value)}
              fullWidth
              required
              error={Boolean(formErrors.username)}
              helperText={formErrors.username}
            />
            <TextField
              label={t("users.fields.firstName")}
              value={addFormData.firstName}
              onChange={(e) => handleAddFormChange("firstName", e.target.value)}
              fullWidth
            />
            <TextField
              label={t("users.fields.lastName")}
              value={addFormData.lastName}
              onChange={(e) => handleAddFormChange("lastName", e.target.value)}
              fullWidth
            />
            <TextField
              label={t("users.fields.email")}
              value={addFormData.email}
              onChange={(e) => handleAddFormChange("email", e.target.value)}
              fullWidth
              type="email"
              required
              error={Boolean(formErrors.email)}
              helperText={formErrors.email}
            />
            <TextField
              label={t("users.fields.group1")}
              value={addFormData.group1}
              onChange={(e) => handleAddFormChange("group1", e.target.value)}
              fullWidth
            />
            <TextField
              label={t("users.fields.group2")}
              value={addFormData.group2}
              onChange={(e) => handleAddFormChange("group2", e.target.value)}
              fullWidth
            />
            <TextField
              label={t("users.fields.group3")}
              value={addFormData.group3}
              onChange={(e) => handleAddFormChange("group3", e.target.value)}
              fullWidth
            />
            <FormControl fullWidth required error={Boolean(formErrors.roles)}>
              <InputLabel>{t("users.fields.roles")} *</InputLabel>
              <Select value={addFormData.roles} onChange={handleAddRoleChange}>
                <MenuItem value="user">{t("users.roles.user")}</MenuItem>
                <MenuItem value="admin">{t("users.roles.admin")}</MenuItem>
                <MenuItem value="serviceUser">
                  {t("users.roles.serviceUser")}
                </MenuItem>
                <MenuItem value="superAdmin">
                  {t("users.roles.superAdmin")}
                </MenuItem>
              </Select>
              {formErrors.roles && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {formErrors.roles}
                </Typography>
              )}
            </FormControl>
            <TextField
              label="Read Only Access"
              value={addFormData.readOnlyAccess}
              onChange={(e) =>
                handleAddFormChange(
                  "readOnlyAccess",
                  parseInt(e.target.value) || 0
                )
              }
              fullWidth
              type="number"
            />
            <TextField
              label="Read Write Access"
              value={addFormData.readWriteAccess}
              onChange={(e) =>
                handleAddFormChange(
                  "readWriteAccess",
                  parseInt(e.target.value) || 0
                )
              }
              fullWidth
              type="number"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={addFormData.enabled}
                  onChange={(e) =>
                    handleAddFormChange("enabled", e.target.checked)
                  }
                />
              }
              label={t("users.fields.enabled")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddDialogClose}>{t("common.cancel")}</Button>
          <Button
            onClick={handleAddSubmit}
            variant="contained"
            disabled={addLoading}
          >
            {addLoading ? <CircularProgress size={20} /> : "Add User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog
        open={passwordResetDialogOpen}
        onClose={handlePasswordResetDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("users.dialogs.resetPassword.title") || "Reset Password"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label={t("users.fields.newPassword") || "New Password"}
              type="password"
              value={passwordResetForm.newPassword}
              onChange={(e) =>
                handlePasswordResetFormChange("newPassword", e.target.value)
              }
              fullWidth
              required
            />
            <TextField
              label={t("users.fields.confirmPassword") || "Confirm Password"}
              type="password"
              value={passwordResetForm.confirmPassword}
              onChange={(e) =>
                handlePasswordResetFormChange("confirmPassword", e.target.value)
              }
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordResetDialogClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handlePasswordResetSubmit}
            variant="contained"
            disabled={passwordResetLoading}
            color="primary"
          >
            {passwordResetLoading ? (
              <CircularProgress size={20} />
            ) : (
              t("users.actions.resetPassword") || "Reset Password"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
